/**
 * Settings › Security — turning two-step verification OFF. Needs the
 * current password plus a code from the authenticator app (or a recovery
 * code, for someone who lost their phone). The server re-mints the session
 * at aal1; in the native shell the new id comes back in the body.
 */
import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, FormField, Input, PasswordInput } from '@jumpstone/ui-react';
import { getTrpcClient } from '../../lib/trpc.js';
import { isNative } from '../../lib/platform.js';
import { nativeClientInput } from '../../lib/auth/native_shell_auth.js';
import { storeReplacementSession } from './store_replacement_session.js';
import {
  classifyTwoFactorError,
  isAuthenticatorCode,
  isRecoveryCode,
  normaliseTwoFactorCode,
  type TwoFactorErrorKind,
} from '../../lib/auth/two_factor_logic.js';
import { TwoFactorError } from '../../components/auth/two_factor_error.js';

export function SecurityDisableForm({
  onCancel,
  onDisabled,
}: {
  onCancel: () => void;
  onDisabled: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<TwoFactorErrorKind | null>(null);

  async function submit(): Promise<void> {
    const normalised = normaliseTwoFactorCode(code);
    if (!isAuthenticatorCode(normalised) && !isRecoveryCode(normalised)) {
      setError('invalid_code');
      return;
    }
    setBusy(true);
    setError(null);
    const native = isNative();
    try {
      const result = await getTrpcClient().wageproTwoFactor.disable.mutate({
        current_password: password,
        code: normalised,
        ...nativeClientInput(native),
      });
      if (!(await storeReplacementSession(result, native))) return;
      onDisabled();
    } catch (err) {
      setError(classifyTwoFactorError(err));
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      className="flex flex-col gap-4"
    >
      <p className="text-sm text-[color:var(--ink-muted)]">
        {t('two_factor.disable_intro')}
      </p>
      <FormField
        label={t('two_factor.code_or_recovery_label')}
        htmlFor="disable-2fa-code"
      >
        <Input
          id="disable-2fa-code"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          autoComplete="one-time-code"
          disabled={busy}
          className="mono max-w-[200px]"
        />
      </FormField>
      <FormField
        label={t('two_factor.current_password_label')}
        htmlFor="disable-2fa-password"
      >
        <PasswordInput
          id="disable-2fa-password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={busy}
          className="max-w-sm"
          labels={{
            show_password: t('login.show_password'),
            hide_password: t('login.hide_password'),
          }}
        />
      </FormField>
      {error !== null && <TwoFactorError kind={error} />}
      <div className="flex gap-2">
        <Button type="submit" variant="danger" loading={busy}>
          {t('two_factor.turn_off')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={busy}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}
